import { useState, useEffect, useMemo } from "react";
import { useFormik, getIn, useFormikContext } from "formik";
import * as Yup from 'yup';
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { IRenter } from "@/types/renter.type";
import { cancelSubscription } from "@/common/services/renters.api";
import { updateVehicleVisualRecords } from "@/common/services/vehicles.api";
import { InstallmentOption, useCalculateInstallmentOptions } from "@/modules/renters/helpers/calculationUtils";
import { BILLING_TYPE } from "@/types/payment.type";

const validationSchema = Yup.object().shape({
  reason: Yup.string().required('Motivo é obrigatório'),
  additionalComments: Yup.string().required('Comentarios adicionais é obrigatório'),
  paymentMethod: Yup.string().when('valueTotalBoolean', {
    is: true,
    then: (schema) => schema.required('Método de pagamento é obrigatório'),
  }),
  paymentDescription: Yup.string().when('valueTotalBoolean', {
    is: true,
    then: (schema) => schema.required('Descrição do pagamento é obrigatória'),
  }),
  billingPeriod: Yup.string().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Período de cobrança é obrigatório'),
  }),
  installmentOption: Yup.string().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Período de cobrança é obrigatório'),
  }),
  subscriptionInitialDate: Yup.date().when('paymentMethod', {
    is: (value) => value === 'credit' || value === 'boleto',
    then: (schema) => schema.required('Data de cobrança é obrigatória'),
  }),
});

type UseCancelPlanProps = {
  renter: IRenter;
  vehicleId: string;
  daysRemaining: number;
};

export const useCancelPlan = ({ renter, vehicleId, daysRemaining }: UseCancelPlanProps) => {
  const { notifySuccess, notifyError } = useToastContext();
  const [isChecked, setIsChecked] = useState(false);

  // Contexto do Formik pai (para registros de fotos)
  const formikContext = useFormikContext();
  const registers = getIn(formikContext?.values, 'registers');
  const nameRenter = getIn(formikContext?.values, 'renterName');
  const returnReason = getIn(formikContext?.values, 'returnReason');
  const licensePlate = getIn(formikContext?.values, 'licensePlate');

  // Constantes derivadas
  const firstInstallmentDate = renter.activationData?.plan?.subscriptionInitialDate;
  const caucao = renter.activationData.plan?.deposit;
  const valueWeeks = renter.activationData.plan?.value || 0;
  const dailyValue = parseFloat(renter?.activationData?.plan?.price?.replace('R$', '').replace(',', '.') || "0");
  const finePlanValue = ((dailyValue * daysRemaining) * 0.10).toFixed(2);

  const paymentOptions = [
    { value: "-", label: "-" },
    { value: BILLING_TYPE.PIX, label: "PIX( Para Pagamento na hora)" },
    { value: BILLING_TYPE.CREDIT_CARD, label: "CRÉDITO (Conferir no Asaas)" },
    { value: BILLING_TYPE.BOLETO, label: "BOLETO" },
  ];

  const cancellationReasonOptions = [
    { value: "", label: "-" },
    { value: "Inadimplencia", label: "Inadimplência" },
    { value: "quebraContrato", label: "Quebra de Contrato" },
    { value: "recolhimento", label: "Mal uso" },
  ];

  // Formik para o cancelamento
  const formik = useFormik({
    initialValues: {
      fields: [],
      reason: "",
      caucao: caucao,
      sinister: 0,
      dailyValue: dailyValue,
      valueWeeks: valueWeeks,
      billingPeriod: "",
      paymentMethod: "",
      installmentOption: "",
      paymentOptions: paymentOptions,
      isCheckedCommandBlock: false,
      isPostWeekChecked: false,
      isWeeklyLate: false,
      additionalComments: "",
      paymentDescription: "",
      subscriptionInitialDate: firstInstallmentDate,
      valueTotalBoolean: false,
      finalFineValue: 0,
      amountValue: 0,
      isCheckedRetraction: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      const receivedData = {
        renterId: renter._id,
        fineDetran: data.fields,
        additionalComments: data.additionalComments,
        paymentDescription: data.paymentDescription,
        valueBlockCommand: data.isCheckedCommandBlock,
        vehicleId: vehicleId,
        customerId: renter.externalReferences.paymentCustomerId,
        paymentMethod: data.paymentMethod,
        installmentOptions: data.installmentOption,
        isCheckedRetraction: data.isCheckedRetraction,
        sinister: data.sinister,
        billingPeriod: data.billingPeriod,
        subscriptionInitialDate: data.subscriptionInitialDate,
        caucao: caucao?.toString(),
        finalFineValue: data.finalFineValue,
        dailyValue: dailyValue,
        finePlanValue: parseFloat(finePlanValue),
        reason: data.reason,
        amoutnValue: data.amountValue,
        isPostWeekChecked: data.isPostWeekChecked,
        valueBilling: Math.max(0, data.finalFineValue - (caucao || 0)),
      };

      const result = await cancelSubscription(receivedData);

      if (!result.ok) {
        if (Array.isArray(result.message)) {
          result.message.forEach((message: string) => {
            notifyError(message);
          });
        } else {
          notifyError(result.message as string);
        }
        return;
      }

      notifySuccess("Contrato cancelado com sucesso");

      // Salvar fotos após cancelamento bem-sucedido
      const savePhotoDelivery = {
        renterId: renter._id,
        vehicleId: vehicleId,
        photos: registers,
        nameRenter: nameRenter,
        licensePlate: licensePlate,
        returnReason: returnReason,
      };

      const resultPhoto = await updateVehicleVisualRecords(savePhotoDelivery);

      if (!resultPhoto.ok) {
        notifyError(resultPhoto.message as string);
        return;
      }

      notifySuccess("Fotos salvas com sucesso");
    },
  });

  // Handler para adicionar registro de fotos
  const onAddRegister = () => {
    formikContext.setFieldValue('registers', [
      ...registers,
      { type: '', content: '', verified: false }
    ]);
  };

  // Callback para receber valores calculados do componente de valores adicionais
  const handleTotalChange = ({
    initalValue,
    amountValue,
    isCheckedRetraction,
    isCheckedCommandBlock,
    valuesSinister,
    isPostWeekChecked,
    isWeeklyLate
  }: {
    initalValue: number;
    amountValue: number;
    isCheckedRetraction: boolean;
    isCheckedCommandBlock: boolean;
    valuesSinister: number;
    isPostWeekChecked: boolean;
    isWeeklyLate: boolean;
  }) => {
    formik.setFieldValue('finalFineValue', initalValue);
    formik.setFieldValue('amountValue', amountValue);
    formik.setFieldValue('isCheckedRetraction', isCheckedRetraction);
    formik.setFieldValue('isCheckedCommandBlock', isCheckedCommandBlock);
    formik.setFieldValue('sinister', valuesSinister);
    formik.setFieldValue('isPostWeekChecked', isPostWeekChecked);
    formik.setFieldValue('isWeeklyLate', isWeeklyLate);
  };

  // Handler para toggle de multas de trânsito
  const handleTrafficFinesToggle = (checked: boolean) => {
    setIsChecked(checked);
    formik.setFieldValue("isChecked", checked);
  };

  // Atualizar valueTotalBoolean quando valueTotal mudar
  const valueTotal = formik.values.finalFineValue;
  useEffect(() => {
    formik.setFieldValue('valueTotalBoolean', valueTotal > (caucao || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTotal, caucao]);

  // Calcular opções de parcelamento
  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions((valueTotal - (caucao || 0)), 24);
  }, [valueTotal, caucao, calculateInstallmentOptions]);

  return {
    // Formik
    formik,

    // Estados
    isChecked,

    // Dados derivados
    caucao,
    valueWeeks,
    dailyValue,
    finePlanValue,
    paymentOptions,
    cancellationReasonOptions,
    installmentOptions,

    // Dados do contexto
    registers,
    nameRenter,

    // Handlers
    onAddRegister,
    handleTotalChange,
    handleTrafficFinesToggle,
  };
};